import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button, Input, Label, BrandMark } from "../components/ui-kit";
import PasswordInput from "../components/PasswordInput";
import OAuthButtons from "../components/OAuthButtons";

export default function Register() {
  const [role, setRole] = useState("customer");
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "+243",
    password: "",
    city_id: "",
    referral_code: "",
    vehicle_type: "moto",
    vehicle_plate: "",
    vehicle_model: "",
    legal_full_name: "",
    id_number: "",
    home_address: "",
    operating_address: "",
    emergency_contact_name: "",
    emergency_contact_phone: "+243",
  });
  const [submitting, setSubmitting] = useState(false);
  const [pendingRegistration, setPendingRegistration] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/cities").then(({ data }) => {
      setCities(Array.isArray(data) ? data : []);
    }).catch(() => setCities([]));
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, role });
      setPendingRegistration(data);
      toast.success("Code envoye par email");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmRegistration = async (e) => {
    e.preventDefault();
    if (!pendingRegistration?.pending_registration_id) return;
    setSubmitting(true);
    try {
      await api.post("/auth/register/confirm", {
        pending_registration_id: pendingRegistration.pending_registration_id,
        code: verificationCode,
      });
      await refresh();
      toast.success("Compte verifie - bienvenue sur Nexora");
      navigate(role === "driver" ? "/driver" : "/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const vehicleTypes = [
    { k: "moto", l: "Moto" },
    { k: "economy", l: "Economy" },
    { k: "comfort", l: "Comfort" },
    { k: "premium", l: "Premium" },
    { k: "xl", l: "XL" },
  ];

  if (pendingRegistration) {
    return (
      <div className="relative app-screen bg-premium-grad grain text-foreground">
        <div className="mx-auto flex app-screen max-w-md flex-col items-center justify-center px-5 auth-screen-safe">
          <Link to="/" className="mb-8" data-testid="brand-back-home"><BrandMark /></Link>
          <div className="w-full rounded-3xl glass-strong p-6 glow-blue">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-primary">Verification email</div>
            <h1 className="mt-1 font-heading text-3xl font-bold">Valider le code</h1>
            <div className="mt-2 rounded-2xl border border-primary/30 bg-primary/10 p-4 text-sm text-muted-foreground">
              Un code a ete envoye a <span className="font-bold text-foreground">{pendingRegistration.email}</span>.
              Le compte sera cree seulement apres validation.
            </div>
            <form onSubmit={confirmRegistration} className="mt-5 space-y-4" data-testid="register-confirm-form">
              <div>
                <Label>Code de verification</Label>
                <Input
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  inputMode="numeric"
                  required
                  data-testid="register-verification-code"
                />
              </div>
              <Button
                type="submit"
                variant="secondary"
                className="w-full py-4 text-base"
                disabled={submitting || !verificationCode}
                data-testid="register-confirm-submit"
              >
                {submitting ? "Verification..." : "Valider et creer mon compte"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setPendingRegistration(null);
                  setVerificationCode("");
                }}
              >
                Modifier mes informations
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative app-screen bg-premium-grad grain text-foreground">
      <div className="mx-auto flex app-screen max-w-lg flex-col items-center justify-center px-5 auth-screen-safe">
        <Link to="/" className="mb-8" data-testid="brand-back-home"><BrandMark /></Link>

        <div className="w-full rounded-3xl glass-strong p-6 glow-blue">
          <h1 className="font-heading text-3xl font-bold">Créer un compte</h1>
          <div className="text-sm text-muted-foreground">Rejoignez la mobilité premium RDC</div>

          <div className="mt-4 grid grid-cols-2 gap-2 rounded-full border border-white/10 bg-white/5 p-1">
            {["customer", "driver"].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRole(r)}
                className={`rounded-full py-2 text-xs font-bold uppercase tracking-widest transition-all ${
                  role === r
                    ? "bg-primary text-primary-foreground glow-blue"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`role-${r}`}
              >
                {r === "customer" ? "Client" : "Chauffeur"}
              </button>
            ))}
          </div>

          {role === "customer" && (
            <div className="mt-5">
              <OAuthButtons role="customer" />
            </div>
          )}

          <form onSubmit={submit} className="mt-5 space-y-3" data-testid="register-form">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>Nom complet</Label>
                <Input value={form.name} onChange={onChange("name")} required data-testid="register-name" />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input value={form.phone} onChange={onChange("phone")} required data-testid="register-phone" />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={onChange("email")} required data-testid="register-email" />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>Mot de passe</Label>
                <PasswordInput value={form.password} onChange={onChange("password")} required minLength={8} autoComplete="new-password" data-testid="register-password" />
                <div className="mt-1 text-[10px] text-muted-foreground">8+ caracteres, majuscule, minuscule, chiffre et symbole.</div>
              </div>
              <div>
                <Label>Ville</Label>
                <select
                  value={form.city_id}
                  onChange={onChange("city_id")}
                  required
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-foreground focus:border-primary focus:outline-none"
                  data-testid="register-city"
                >
                  <option value="" className="bg-background">
                    Choisir ma ville
                  </option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.id} className="bg-background">
                      {c.name} — {c.province}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label>Code parrain (optionnel)</Label>
              <Input value={form.referral_code} onChange={onChange("referral_code")} placeholder="NEXO-AM01" data-testid="register-referral" />
            </div>

            {role === "driver" && (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="mb-3 rounded-xl border border-primary/25 bg-primary/10 p-3 text-[11px] text-muted-foreground">
                  Un chauffeur Nexora peut aussi recevoir des livraisons. Les roles livreur, agent, marchand ou admin sont attribues depuis l'administration.
                </div>
                <div className="mb-3 text-[11px] font-bold uppercase tracking-widest text-primary">Véhicule</div>
                <div className="grid grid-cols-2 gap-1.5 min-[390px]:grid-cols-3 sm:grid-cols-5">
                  {vehicleTypes.map((v) => (
                    <button
                      key={v.k}
                      type="button"
                      onClick={() => setForm({ ...form, vehicle_type: v.k })}
                      className={`rounded-lg py-2 text-[10px] font-bold uppercase tracking-wider ${
                        form.vehicle_type === v.k
                          ? "bg-primary text-primary-foreground"
                          : "bg-white/5 text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`vehicle-${v.k}`}
                    >
                      {v.l}
                    </button>
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <Label>Plaque</Label>
                    <Input value={form.vehicle_plate} onChange={onChange("vehicle_plate")} placeholder="PROV 1234" data-testid="vehicle-plate" />
                  </div>
                  <div>
                    <Label>Modèle</Label>
                    <Input value={form.vehicle_model} onChange={onChange("vehicle_model")} placeholder="Boxer 150" data-testid="vehicle-model" />
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <Label>Identite complete</Label>
                    <Input value={form.legal_full_name} onChange={onChange("legal_full_name")} placeholder="Nom legal complet" required />
                  </div>
                  <div>
                    <Label>Numero piece</Label>
                    <Input value={form.id_number} onChange={onChange("id_number")} placeholder="ID / passeport" required />
                  </div>
                </div>
                <div className="mt-3">
                  <Label>Adresse complete</Label>
                  <Input value={form.home_address} onChange={onChange("home_address")} placeholder="Commune, quartier, avenue, numero" required />
                </div>
                <div className="mt-3">
                  <Label>Zone ou adresse de travail</Label>
                  <Input value={form.operating_address} onChange={onChange("operating_address")} placeholder="Zone habituelle de service" required />
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <Label>Contact urgence</Label>
                    <Input value={form.emergency_contact_name} onChange={onChange("emergency_contact_name")} required />
                  </div>
                  <div>
                    <Label>Telephone urgence</Label>
                    <Input value={form.emergency_contact_phone} onChange={onChange("emergency_contact_phone")} required />
                  </div>
                </div>
                <div className="mt-3 rounded-xl border border-warning/30 bg-warning/10 p-3 text-[11px] text-muted-foreground">
                  Obligatoire apres inscription: photo piece d'identite, permis, carte grise et photo de la plaque.
                </div>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              className="w-full py-4 text-base"
              disabled={submitting}
              data-testid="register-submit"
            >
              {submitting ? "Création..." : "Créer mon compte"}
            </Button>
          </form>

          <div className="mt-5 border-t border-white/10 pt-3 text-center text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Déjà inscrit ?{" "}
            <Link to="/login" className="text-primary" data-testid="go-login">Se connecter</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
